import React from "react";
import {
  FiEdit2,
  FiExternalLink,
  FiEye,
  FiGlobe,
  FiMail,
  FiMapPin,
  FiPhone,
  FiServer,
  FiToggleLeft,
  FiToggleRight,
  FiTrash2,
} from "react-icons/fi";
import { SlEarphonesAlt } from "react-icons/sl";

import AdminButton from "../../_shared/ui/AdminButton";
import {
  ActiveBadge,
  PublicBadge,
} from "./ServiceMunicipalStatusBadges";

import {
  formatDateTime,
  getGoogleMapsUrl,
  getServiceTypeLabel,
  normalizeExternalUrl,
  truncateText,
} from "../utils/servicesMunicipauxAdminUtils";

export default function ServiceMunicipalCard({
  item,
  actionId,
  onPreview,
  onEdit,
  onToggleActive,
  onTogglePublic,
  onAskDelete,
}) {
  const activeLoading = actionId === `active-${item.id}`;
  const publicLoading = actionId === `public-${item.id}`;
  const mapsUrl = getGoogleMapsUrl(item.gps_service);

  return (
    <article className="group overflow-hidden rounded-[1.7rem] border border-slate-200 bg-slate-50 transition hover:-translate-y-0.5 hover:border-sky-200 hover:bg-white hover:shadow-xl hover:shadow-slate-200/60 dark:border-slate-800 dark:bg-slate-950 dark:hover:border-sky-500/30 dark:hover:bg-slate-900 dark:hover:shadow-black/20">
      <div className="relative bg-gradient-to-br from-cyan-700 via-sky-600 to-indigo-600 p-5 text-white">
        <div className="pointer-events-none absolute -right-12 -top-12 h-32 w-32 rounded-full bg-white/15 blur-2xl" />

        <div className="relative flex items-start justify-between gap-4">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-[1.25rem] border border-white/20 bg-white/15 text-2xl shadow-lg backdrop-blur">
            {item.icone_service ? (
              <span className="text-sm font-bold">{item.icone_service}</span>
            ) : (
              <SlEarphonesAlt />
            )}
          </div>

          <div className="flex flex-wrap justify-end gap-2">
            <ActiveBadge active={!!item.is_active} />
            <PublicBadge isPublic={!!item.is_public} />
          </div>
        </div>

        <div className="relative mt-5">
          <div className="flex flex-wrap gap-2">
            {item.categorie_service ? (
              <span className="rounded-full border border-white/20 bg-white/15 px-3 py-1 text-xs font-bold text-white/90">
                {item.categorie_service}
              </span>
            ) : null}

            {item.type_service ? (
              <span className="rounded-full border border-white/20 bg-white/15 px-3 py-1 text-xs font-bold text-white/90">
                {getServiceTypeLabel(item.type_service)}
              </span>
            ) : null}
          </div>

          <h3 className="mt-4 line-clamp-2 text-xl font-bold leading-snug">
            {item.nom_service || "Service municipal"}
          </h3>

          {item.accroche_service ? (
            <p className="mt-2 line-clamp-2 text-sm leading-6 text-white/80">
              {item.accroche_service}
            </p>
          ) : null}
        </div>
      </div>

      <div className="flex flex-col p-4 sm:p-5">
        {item.slug_service ? (
          <div className="mb-3 inline-flex w-fit rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-500 dark:bg-slate-800 dark:text-slate-400">
            /services/{item.slug_service}
          </div>
        ) : null}

        <p className="line-clamp-3 text-sm leading-6 text-slate-600 dark:text-slate-300">
          {truncateText(item.description_service, 220) ||
            truncateText(item.infos_complementaires, 220) ||
            "Aucune description renseignée."}
        </p>

        <div className="mt-4 flex flex-wrap gap-2 text-xs font-bold text-slate-500 dark:text-slate-400">
          {item.tel_service ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1.5 dark:bg-slate-800">
              <FiPhone />
              {item.tel_service}
            </span>
          ) : null}

          {item.mail_service ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1.5 dark:bg-slate-800">
              <FiMail />
              {item.mail_service}
            </span>
          ) : null}

          {item.ville_service ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1.5 dark:bg-slate-800">
              <FiMapPin />
              {item.cp_service ? `${item.cp_service} ` : ""}
              {item.ville_service}
            </span>
          ) : null}

          {item.website_service ? (
            <a
              href={normalizeExternalUrl(item.website_service)}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-3 py-1.5 text-sky-700 transition hover:bg-sky-100 dark:bg-sky-500/10 dark:text-sky-300"
            >
              <FiGlobe />
              Site web
            </a>
          ) : null}

          {mapsUrl ? (
            <a
              href={mapsUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-3 py-1.5 text-amber-700 transition hover:bg-amber-100 dark:bg-amber-500/10 dark:text-amber-300"
            >
              <FiMapPin />
              Carte
            </a>
          ) : null}
        </div>

        <div className="mt-4 flex flex-wrap gap-3 text-xs font-bold text-slate-400 dark:text-slate-500">
          <span>Créé le {formatDateTime(item.created_at)}</span>
          <span>Ordre : {item.ordre_affichage ?? 0}</span>
          {item.parent_id ? <span>Parent : {item.parent_id}</span> : null}
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          <AdminButton
            icon={FiEye}
            variant="secondary"
            size="sm"
            onClick={() => onPreview(item.id)}
          >
            Voir
          </AdminButton>

          <AdminButton
            icon={FiEdit2}
            variant="soft"
            size="sm"
            onClick={() => onEdit(item.id)}
          >
            Modifier
          </AdminButton>

          <AdminButton
            icon={item.is_active ? FiToggleLeft : FiToggleRight}
            variant={item.is_active ? "secondary" : "primary"}
            size="sm"
            loading={activeLoading}
            onClick={() => onToggleActive(item.id)}
          >
            {item.is_active ? "Désactiver" : "Activer"}
          </AdminButton>

          <AdminButton
            icon={item.is_public ? FiToggleLeft : FiToggleRight}
            variant={item.is_public ? "secondary" : "primary"}
            size="sm"
            loading={publicLoading}
            onClick={() => onTogglePublic(item.id)}
          >
            {item.is_public ? "Masquer" : "Afficher"}
          </AdminButton>

          <AdminButton
            icon={FiTrash2}
            variant="dangerSoft"
            size="sm"
            onClick={() => onAskDelete(item.id)}
          >
            Supprimer
          </AdminButton>
        </div>
      </div>
    </article>
  );
}